<template>
  <div class="focal">
    <div class="subtitle-2 mb-1">Choose what stays visible</div>
    <div class="caption mb-2">
      Click or drag on the image, on the part that must not be cut off. The blog
      cards crop the image to fit and keep that part in view.
    </div>

    <!-- Full image with the chosen point -->
    <div
      ref="area"
      class="focal__area"
      @pointerdown.prevent="onDown"
      @pointermove="onMove"
      @pointerup="onUp"
      @pointercancel="onUp"
    >
      <img
        :src="src"
        alt=""
        class="focal__img"
        draggable="false"
        @load="failed = false"
        @error="failed = true"
      />
      <span
        v-if="!failed"
        class="focal__dot"
        :style="{ left: x + '%', top: y + '%' }"
      />
    </div>

    <!-- The image could not be loaded from its saved address -->
    <v-alert v-if="failed" type="warning" dense text class="mt-2 mb-0 caption">
      The image could not be loaded from
      <span class="focal__url">{{ src }}</span>
      <br />
      The file may not exist at that address, for example if it was uploaded
      while the API pointed to another server. Remove the cover and upload it
      again.
    </v-alert>

    <!-- How it will look -->
    <template v-else>
      <div class="caption mt-3 mb-1">Blog card</div>
      <div class="focal__preview focal__preview--card">
        <img :src="src" alt="" class="focal__preview-img" :style="imgStyle" />
      </div>

      <div class="caption mt-3 mb-1">Featured (main) card</div>
      <div class="focal__preview focal__preview--main">
        <img :src="src" alt="" class="focal__preview-img" :style="imgStyle" />
      </div>
    </template>

    <v-btn x-small text class="mt-2" @click="reset">Reset to center</v-btn>
  </div>
</template>

<script>
const DEFAULT = "50% 50%";

const clamp = (n) => Math.min(100, Math.max(0, Math.round(n)));

export default {
  name: "BlogCoverFocalPicker",
  props: {
    // The image URL
    src: { type: String, required: true },
    // The chosen point as "x% y%", for example "50% 30%"
    value: { type: String, default: DEFAULT },
  },
  data() {
    return { dragging: false, failed: false };
  },
  computed: {
    point() {
      const m = /^(\d{1,3})%\s+(\d{1,3})%$/.exec(this.value || "");
      return m ? [clamp(+m[1]), clamp(+m[2])] : [50, 50];
    },
    x() {
      return this.point[0];
    },
    y() {
      return this.point[1];
    },
    // Same crop rule as the public site (cover + anchor point)
    imgStyle() {
      return { objectPosition: `${this.x}% ${this.y}%` };
    },
  },
  watch: {
    // A different image gets a fresh chance to load.
    src() {
      this.failed = false;
    },
  },
  methods: {
    setFromEvent(e) {
      const rect = this.$refs.area.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const x = clamp(((e.clientX - rect.left) / rect.width) * 100);
      const y = clamp(((e.clientY - rect.top) / rect.height) * 100);

      this.$emit("input", `${x}% ${y}%`);
    },
    onDown(e) {
      this.dragging = true;
      // Keep receiving the pointer even when it leaves the image while dragging
      if (e.target.setPointerCapture) {
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch (err) {
          // not supported, dragging still works inside the image
        }
      }
      this.setFromEvent(e);
    },
    onMove(e) {
      if (this.dragging) this.setFromEvent(e);
    },
    onUp() {
      this.dragging = false;
    },
    reset() {
      this.$emit("input", DEFAULT);
    },
  },
};
</script>

<style scoped>
.focal__area {
  position: relative;
  display: inline-block;
  max-width: 100%;
  line-height: 0;
  border-radius: 8px;
  overflow: hidden;
  cursor: crosshair;
  touch-action: none;
  user-select: none;
}

.focal__img {
  display: block;
  max-width: 100%;
  max-height: 260px;
}

.focal__dot {
  position: absolute;
  width: 22px;
  height: 22px;
  margin: -11px 0 0 -11px;
  border: 3px solid #ffffff;
  border-radius: 50%;
  background: rgba(245, 158, 11, 0.9);
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.5);
  pointer-events: none;
}

.focal__preview {
  overflow: hidden;
  background-color: #0f1d38;
  border-radius: 8px;
}

.focal__preview-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.focal__url {
  word-break: break-all;
  font-family: monospace;
}

/* About the same shape as the card cover on the public page */
.focal__preview--card {
  width: 100%;
  aspect-ratio: 2.4 / 1;
}

/* About the same shape as the left side of the featured card */
.focal__preview--main {
  width: 70%;
  aspect-ratio: 1.15 / 1;
}
</style>
